/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * "Ho dimenticato la password": si scrive l'indirizzo e si riceve un link.
 *
 * La schermata di conferma NON dice se l'indirizzo esiste, perche' non lo dice
 * nemmeno il server: risponderebbe uguale in ogni caso. Sembra una sfumatura ed
 * e' il punto — se dicesse "utente non trovato", chiunque potrebbe usare questa
 * pagina per scoprire chi e' cliente e chi no.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MailCheck, ArrowLeft } from 'lucide-react';
import { useImpostazioni, urlImmagine } from '../hooks/useImpostazioni';
import { authAPI } from '../api/auth';

const ACCENT = '#10B981';

export default function PasswordDimenticata() {
  const [email, setEmail] = useState('');
  const [inviata, setInviata] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const impostazioni = useImpostazioni();
  const logo = urlImmagine(impostazioni.app_logo_url);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrore('');
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim().toLowerCase());
      setInviata(true);
    } catch (err) {
      // Il server risponde 200 anche quando non manda niente: qui si arriva solo
      // se la rete o l'API sono giu' davvero.
      setErrore('Non riesco a contattare il server. Riprova fra poco.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {logo && <img src={logo} alt="" style={styles.logo} />}
        <h1 style={{ ...styles.title, color: ACCENT }}>Password dimenticata</h1>
        <p style={styles.subtitle}>{impostazioni.app_nome}</p>

        {inviata ? (
          <>
            <div style={styles.okBox}>
              <MailCheck size={22} color="#047857" />
              <div>
                Se l'indirizzo <strong>{email.trim().toLowerCase()}</strong> e' registrato,
                fra poco arrivera' un'email con il link per scegliere una password nuova.
              </div>
            </div>
            <p style={styles.nota}>
              Il link vale <strong>un'ora</strong> e si puo' usare una volta sola.
              Se non arriva niente, controlla la posta indesiderata: l'email potrebbe
              essere finita li'.
            </p>
          </>
        ) : (
          <form onSubmit={onSubmit} style={styles.form}>
            <p style={styles.spiegazione}>
              Scrivi l'indirizzo email con cui accedi. Ti mandiamo un link per
              scegliere una password nuova.
            </p>
            <label style={styles.label}>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                style={styles.input}
              />
            </label>

            {errore && <div style={styles.error}>{errore}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{ ...styles.button, backgroundColor: ACCENT, opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Invio...' : 'Mandami il link'}
            </button>
          </form>
        )}

        <div style={styles.tornaRow}>
          <Link to="/login" style={{ ...styles.tornaLink, color: ACCENT }}>
            <ArrowLeft size={15} /> Torna all'accesso
          </Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F3F4F6', padding: '1rem' },
  card: { backgroundColor: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.07)', width: '100%', maxWidth: '400px', boxSizing: 'border-box' },
  logo: { width: '64px', height: '64px', objectFit: 'contain', display: 'block', margin: '0 auto 0.75rem' },
  title: { fontSize: '1.4rem', fontWeight: 700, textAlign: 'center', margin: '0 0 0.25rem' },
  subtitle: { textAlign: 'center', color: '#6B7280', fontSize: '0.9rem', margin: '0 0 1.25rem' },
  spiegazione: { fontSize: '0.88rem', color: '#4B5563', margin: '0 0 0.5rem', lineHeight: 1.5 },
  form: { display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  label: { display: 'flex', flexDirection: 'column', gap: '0.35rem', fontSize: '0.85rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.65rem 0.75rem', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' },
  button: { padding: '0.7rem', color: 'white', border: 'none', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer', minHeight: '44px' },
  error: { padding: '0.6rem 0.75rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.85rem' },
  okBox: { display: 'flex', gap: '0.65rem', alignItems: 'flex-start', padding: '0.85rem', backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0', color: '#065F46', borderRadius: '10px', fontSize: '0.88rem', lineHeight: 1.5 },
  nota: { fontSize: '0.8rem', color: '#6B7280', marginTop: '0.85rem', lineHeight: 1.5 },
  tornaRow: { marginTop: '1.25rem', textAlign: 'center' },
  tornaLink: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.88rem', fontWeight: 600, textDecoration: 'none' },
};
